import { Fragment } from 'react';
import Link from 'next/link';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

interface BreadcrumbProps {
  label: string;
  href: string;
}

interface Props {
  children: React.ReactNode;
  breadcrumbs?: BreadcrumbProps[];
  right?: React.ReactNode;
  title: React.ReactNode;
  disableOverflow?: boolean;
}

export function PageMain({ children, breadcrumbs = [], title, right, disableOverflow }: Props) {
  return (
    <div className={cn('flex w-full flex-col', disableOverflow && 'h-[calc(100vh-1rem)] overflow-hidden')}>
      <PageHeader breadcrumbs={breadcrumbs} title={title} right={right} />
      <div className={cn('flex flex-1 flex-col gap-4 p-4', disableOverflow && 'overflow-hidden')}>{children}</div>
    </div>
  );
}

interface PageHeaderProps {
  title: React.ReactNode;
  breadcrumbs?: BreadcrumbProps[];
  right?: React.ReactNode;
}

export function PageHeader({ breadcrumbs = [], title, right }: PageHeaderProps) {
  return (
    <header className='flex h-9 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12'>
      <div className='flex w-full items-center gap-2 px-3 pt-3'>
        <SidebarTrigger />
        <Separator orientation='vertical' className='h-4' />
        <PageBreadcrumbs breadcrumbs={breadcrumbs} title={title} />
        {right && <div className='ml-auto'>{right}</div>}
      </div>
    </header>
  );
}

export function PageBreadcrumbs({ breadcrumbs, title }: { breadcrumbs: BreadcrumbProps[]; title: React.ReactNode }) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        {breadcrumbs?.map((breadcrumb) => (
          <Fragment key={breadcrumb.href}>
            <BreadcrumbItem className='hidden md:block'>
              <BreadcrumbLink href={breadcrumb.href} asChild>
                <Link href={breadcrumb.href}>{breadcrumb.label}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className='hidden md:block' />
          </Fragment>
        ))}

        <BreadcrumbItem>
          <BreadcrumbPage>{title}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}
